import { makeRequest, apiClient } from '../../../../services/apiClient';

// Detect 404 or "Not Found" errors robustly across environments
const isNotFound = (err) => {
  if (!err) return false;
  const status = err?.response?.status;
  if (status === 404) return true;
  const msg = String(err?.message || err || '');
  return /Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg);
};

// Derive backend origin from axios baseURL to avoid using the frontend origin
const getBackendOrigin = () => {
  try {
    const base = apiClient?.defaults?.baseURL || '';
    if (!base) return null;
    const u = new URL(base, window?.location?.origin);
    return u.origin; // e.g., http://localhost:8000
  } catch (e) {
    return null;
  }
};

/**
 * Fetch KPI for events with missing/invalid timestamps.
 * Normalizes to { file_type, total_events, invalid_timestamp_events, invalid_ratio }.
 */
export const fetchMissingTimestampsKPI = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  // Primary path - KPI router is mounted at /api/kpi
  try {
    const res = await makeRequest('/kpi/missing-timestamps', { params }, 'missing-timestamps kpi');
    return normalize(res);
  } catch (err) {
    // Try legacy path without /kpi prefix
    if (isNotFound(err)) {
      try {
        const res = await makeRequest('/missing-timestamps', { params }, 'missing-timestamps kpi (legacy)');
        return normalize(res);
      } catch (err2) {
        if (!isNotFound(err2)) throw err2;
      }
    } else {
      throw err;
    }
  }

  // Absolute URL variants (handle different gateways/prefixes)
  if (typeof window !== 'undefined' && window.location) {
    const origin = getBackendOrigin() || window.location.origin;
    const candidates = [
      // direct paths
      `${origin}/missing-timestamps`,
      `${origin}/api/missing-timestamps`,
      `${origin}/v1/missing-timestamps`,
      `${origin}/api/v1/missing-timestamps`,
      // analytics-scoped paths
      `${origin}/analytics/missing-timestamps`,
      `${origin}/api/analytics/missing-timestamps`,
      `${origin}/api/v1/analytics/missing-timestamps`,
      // common gateway prefixes
      `${origin}/gateway/api/missing-timestamps`,
      `${origin}/gateway/api/v1/missing-timestamps`,
      `${origin}/gateway/api/analytics/missing-timestamps`,
      `${origin}/gateway/api/v1/analytics/missing-timestamps`,
    ];
    for (const url of candidates) {
      try {
        const res = await makeRequest(url, { params }, `missing-timestamps kpi (${url})`);
        return normalize(res);
      } catch (e2) {
        if (!isNotFound(e2)) throw e2;
      }
    }
  }

  // As a final fallback, try the simple count plus counts endpoint to synthesize the KPI
  try {
    // simple endpoint for invalid count
    const origin = (typeof window !== 'undefined' && (apiClient?.defaults?.baseURL || window.location.origin)) || '';
    const simpleCandidates = [
      '/missing-timestamps-simple',
      '/api/missing-timestamps-simple',
      '/v1/missing-timestamps-simple',
      '/api/v1/missing-timestamps-simple',
      '/analytics/missing-timestamps-simple',
      '/api/analytics/missing-timestamps-simple',
      '/api/v1/analytics/missing-timestamps-simple',
    ];
    let missing = null;
    for (const path of simpleCandidates) {
      try {
        const res = await makeRequest(path, { params }, `missing-timestamps simple (${path})`);
        if (res && typeof res.missing_timestamps === 'number') {
          missing = res.missing_timestamps;
          break;
        }
      } catch (e3) {
        if (!isNotFound(e3)) throw e3;
      }
    }

    // get total events from counts
    let total = 0;
    try {
      const counts = await makeRequest('/counts', { params }, 'get counts (fallback for missing timestamps)');
      if (counts && typeof counts.totalEvents === 'number') {
        total = counts.totalEvents;
      }
    } catch (e4) {
      // Try absolute alias if needed
      try {
        const abs = `${origin}/api/counts`;
        const counts = await makeRequest(abs, { params }, `get counts (${abs})`);
        if (counts && typeof counts.totalEvents === 'number') total = counts.totalEvents;
      } catch {}
    }

    if (missing !== null) {
      const ratio = total > 0 ? missing / total : 0;
      return normalize({ file_type: fileType || 'main', total_events: total, invalid_timestamp_events: missing, invalid_ratio: ratio });
    }
  } catch (e5) {
    // ignore and fall through to default zeros
  }

  // If all attempts failed with 404-like behavior, return a graceful default
  return normalize({ file_type: fileType || 'main', total_events: 0, invalid_timestamp_events: 0, invalid_ratio: 0 });
};

const normalize = (raw) => {
  if (!raw || typeof raw !== 'object') return {
    file_type: 'main', total_events: 0, invalid_timestamp_events: 0, invalid_ratio: 0
  };
  const file_type = raw.file_type || 'main';
  const total_events = Number(raw.total_events) || 0;
  const invalid_timestamp_events = Number(raw.invalid_timestamp_events) || 0;
  const invalid_ratio = Number(raw.invalid_ratio) || (total_events > 0 ? invalid_timestamp_events / total_events : 0);
  return { file_type, total_events, invalid_timestamp_events, invalid_ratio, considered_keys: raw.considered_keys, criteria: raw.criteria };
};
